import { Autocomplete, Box, Button, Checkbox, Stack } from '@mui/material';
import { TextField } from '@side/pantry';
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { InactiveChip } from './InactiveChip';
import { useChunkedUsers } from './useChunkedIdentity';

interface Props<T extends FieldValues> {
  control: Control<T, unknown, T>;
  onFilterToOnlyMe: () => void;
  agentIdentityIds?: string[];
  name: Path<T>;
}

export const AgentFilter = <T extends FieldValues>({
  control,
  onFilterToOnlyMe,
  agentIdentityIds = [],
  name,
}: Props<T>) => {
  const { users: agents, isLoading } = useChunkedUsers(agentIdentityIds);
  const agentOptions = [...(agents ?? [])]
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((agent) => ({
      label: agent.displayName,
      value: agent.id,
      status: agent.status,
    }));

  return (
    <Box>
      <Controller
        disabled={isLoading}
        name={name}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Autocomplete
            multiple
            disabled={isLoading}
            options={agentOptions}
            value={value}
            onChange={(_event, newValue) => onChange(newValue)}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.label
            }
            renderOption={(props, option, { selected }) => (
              <Stack
                direction="row"
                component="li"
                {...props}
                sx={{
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center', minWidth: 0, flex: 1 }}
                >
                  <Checkbox
                    disableRipple
                    checked={selected}
                    sx={{ mr: 2, p: 0, flexShrink: 0 }}
                  />
                  {option.label}
                </Stack>
                {option.status !== 'ACTIVE' && <InactiveChip />}
              </Stack>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Agents"
                placeholder="Search for agents"
                fullWidth
              />
            )}
          />
        )}
      />
      <Button variant="link" onClick={onFilterToOnlyMe} disabled={isLoading}>
        Filter to Only Me
      </Button>
    </Box>
  );
};
